import React, { useEffect, useState, useRef } from "react";
import * as CONSTANTS from "common/ulti/constants";
import Dropzone from "react-dropzone";
import { HTTP_API } from "config";
import { CloudDownloadOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";

function FileUploadBase64({
  parentClass,
  labelClass,
  inputClass,
  label,
  multiple,
  required,
  disabled,
  onChange,
  onRemove,
  errorLabel,
  downloadable,
  accept = ".doc,.docx",
  ...props
}) {
  const idRef = useRef(props.id);
  const valueRef = useRef(props.value || []);
  const [_requiredErr, setRequiredErr] = useState(
    required ? { state: true, msg: "" } : { state: false, msg: "" }
  );

  const ERR_REQUIRED = "Vui lòng điền đầy đủ thông tin";

  useEffect(() => {
    if (required && valueRef.current.length === 0) {
      setRequiredErr({ state: true, msg: ERR_REQUIRED });
    } else {
      setRequiredErr({ state: false, msg: "" });
    }
  }, [props.value, required]);

  useEffect(() => {
    valueRef.current = props.value || [];
  }, [props.value]);

  const _collapseFileName = (originName) => {
    if (!originName) {
      if (disabled)
        return <span style={{ cursor: "pointer" }}>Tải xuống để xem</span>;
      return props.placeholder ? `(${props.placeholder})` : "";
    }

    const maxLength = 15;
    const anc = originName.lastIndexOf(".");
    if (anc <= 0) return originName;
    const fn = originName.slice(0, anc);
    const ext = originName.slice(anc + 1);
    if (fn.length > maxLength) {
      const startTrunc = fn.slice(0, maxLength / 2);
      const endTrunc = fn.slice(-maxLength / 2);
      return `${startTrunc}...${endTrunc}.${ext}`;
    }
    return `${fn}.${ext}`;
  };

  const _onDrop = (newFiles) => {
    valueRef.current = newFiles;
    if (required && newFiles.length === 0) {
      setRequiredErr({ state: true, msg: ERR_REQUIRED });
    } else {
      setRequiredErr({ state: false, msg: "" });
    }
    onChange({ target: { id: idRef.current, value: newFiles } });
  };

  const _onRemove = (file, index) => {
    if (disabled) return;
    valueRef.current.splice(index, 1);
    if (required && valueRef.current.length === 0) {
      setRequiredErr({ state: true, msg: ERR_REQUIRED });
    } else {
      setRequiredErr({ state: false, msg: "" });
    }
    onRemove({ target: { id: idRef.current, value: valueRef.current } });
  };

  const _download = (file) => {
    if (!file.filename) return;
    const url = `${HTTP_API}/api/media/download?q=${file.filename}`;
    window.location.href = url;
  };

  const _renderSelectedList = () => {
    return valueRef?.current?.map((file, index) => {
      return (
        <div key={index} className="row-base-line">
          <span
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                props.download ? props.download() : _download(file);
              }
            }}
            style={{ cursor: downloadable ? "pointer" : "default" }}
            className="font-italic"
            title={file?.name}
            onClick={() =>
              props.download ? props.download() : _download(file)
            }
          >
            {downloadable ? <CloudDownloadOutlined /> : null}
          </span>
          {_collapseFileName(file?.name || file?.originalname)}
          <button
            style={{ display: disabled ? "none" : "" }}
            type="button"
            className="btn btn-sm ic-red form-no-spacing"
            onClick={() => _onRemove(file, index)}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
      );
    });
  };

  const _parentClass = `${
    parentClass || "col-md-12"
  } form-row form-wrap form-group`;
  const _wrapperClass = "col-md-12 form-row form-no-spacing";
  const _labelClass = `${labelClass || "col-md-3"} mb-0`;
  const _inputClass = `${
    inputClass || "col-md-9"
  } input-file form-no-spacing mb-0`;
  const selectedCount = valueRef.current.length;
  const placeholder = selectedCount
    ? `Đã chọn ${selectedCount} files`
    : "Bấm để chọn file hoặc kéo thả file vào đây...";

  return (
    <div className={_parentClass}>
      <div className={_wrapperClass}>
        <label className={_labelClass}>
          {label}
          {required ? <span className="required">*</span> : null}
        </label>
        <div className={_inputClass}>
          {!disabled ? (
            multiple || selectedCount != 1 ? (
              <Dropzone
                className="dropzone-files"
                style={{ cursor: "grab" }}
                {...props}
                multiple={multiple}
                accept={accept}
                onDrop={disabled ? null : _onDrop}
              >
                <i className="far fa-folder-open"></i>
                {placeholder}
              </Dropzone>
            ) : null
          ) : null}
          {_renderSelectedList()}
        </div>
      </div>

      {_requiredErr.msg && (
        <label
          className={`${_inputClass} form-label-err`}
          name={CONSTANTS.LABEL_VALID_ERROR_NAME}
        >
          {_requiredErr.msg}
        </label>
      )}
    </div>
  );
}

FileUploadBase64.propTypes = {
  parentClass: PropTypes.string,
  labelClass: PropTypes.string,
  inputClass: PropTypes.string,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  errorLabel: PropTypes.string,
  downloadable: PropTypes.bool,
  accept: PropTypes.string,
  download: PropTypes.func, // 👈 bổ sung dòng này
  id: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,

  // Nếu bạn truyền thêm các prop khác qua ...props, có thể khai báo cụ thể nếu biết rõ
};

export default FileUploadBase64;
